import { useEffect } from 'react';
import { SvgIcon } from '@avantia/style-guide';
import { Button } from 'components/shared/button';
import { Link } from 'react-router-dom';

import { formatAmount, formatDate, getDateWithoutTime } from 'utils';
import { addDays } from 'date-fns';
import { FullPolicyData } from 'interfaces/customerDataTypes';

import './conversionSuccess.scss';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getCustomerInsuranceSummaryAction } from 'actions/getCustomerInsuranceSummaryAction';
import { analyticsUserType, GTM_EVENT_TYPES, sendAnalytics } from 'analytics';

interface ConversionSuccessProps {
  selectedPolicy: FullPolicyData;
  paymentPreference: 'Monthly' | 'Yearly';
  amount?: number;
}

export const ConversionSuccess = ({
  selectedPolicy,
  paymentPreference,
  amount
}: ConversionSuccessProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { policyEndDate, policyNumber, customerRenewalOptOut } = selectedPolicy;
  const policy = useAppSelector((state) => state.policy);
  const authentication = useAppSelector((state) => state.authentication);
  const { isAgent, authType } = authentication;
  const { renewalPaymentReference } = policy;
  const renewalDate = formatDate(addDays(getDateWithoutTime(policyEndDate), 1));

  useEffect(() => {
    sendAnalytics({
      event: GTM_EVENT_TYPES.RENEWAL_CONFIRMATION,
      detail: {
        policy_ref: policyNumber,
        usertype: analyticsUserType(isAgent),
        is_auto_renewal: !customerRenewalOptOut
      }
    });
    return () => {
      dispatch(getCustomerInsuranceSummaryAction({ urlAuthType: authType }));
    };
  }, [dispatch, authType]);

  return (
    <div className="conversion-success-container">
      <SvgIcon type="check-circle-large" className="check-circle-green" />
      <h2 id="conversion-success-heading">Policy will renew on {renewalDate}</h2>
      {paymentPreference === 'Monthly' ? (
        <p className="conversion-success-text">Your policy is now scheduled to renew.</p>
      ) : (
        <div>
          <p className="conversion-success-text">
            <span className="bold">Payment amount:</span> {formatAmount(amount)}
          </p>
          <p className="conversion-success-text">
            <span className="bold">Payment reference:</span> {renewalPaymentReference}
          </p>
        </div>
      )}
      <Link to={`/dashboard/policy/${policyNumber}`}>
        <Button text="Return to my dashboard" variant="secondary" />
      </Link>
    </div>
  );
};
